// NOTE: see `c2c-lesson/ckeditor5/README.md` for build instructions

/**
 * @license Copyright (c) 2014-2022, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
import InlineEditor from '@ckeditor/ckeditor5-editor-inline/src/inlineeditor.js';
import Alignment from '@ckeditor/ckeditor5-alignment/src/alignment.js';
import Autoformat from '@ckeditor/ckeditor5-autoformat/src/autoformat.js';
import BlockQuote from '@ckeditor/ckeditor5-block-quote/src/blockquote.js';
import Bold from '@ckeditor/ckeditor5-basic-styles/src/bold.js';
import CloudServices from '@ckeditor/ckeditor5-cloud-services/src/cloudservices.js';
import Essentials from '@ckeditor/ckeditor5-essentials/src/essentials.js';
import FindAndReplace from '@ckeditor/ckeditor5-find-and-replace/src/findandreplace.js';
import FontBackgroundColor from '@ckeditor/ckeditor5-font/src/fontbackgroundcolor.js';
import FontColor from '@ckeditor/ckeditor5-font/src/fontcolor.js';
import FontFamily from '@ckeditor/ckeditor5-font/src/fontfamily.js';
import FontSize from '@ckeditor/ckeditor5-font/src/fontsize.js';
import Heading from '@ckeditor/ckeditor5-heading/src/heading.js';
import Highlight from '@ckeditor/ckeditor5-highlight/src/highlight.js';
import HorizontalLine from '@ckeditor/ckeditor5-horizontal-line/src/horizontalline.js';
import HtmlComment from '@ckeditor/ckeditor5-html-support/src/htmlcomment.js';
import Image from '@ckeditor/ckeditor5-image/src/image.js';
import ImageResize from '@ckeditor/ckeditor5-image/src/imageresize.js';
import ImageStyle from '@ckeditor/ckeditor5-image/src/imagestyle.js';
import ImageTextAlternative from '@ckeditor/ckeditor5-image/src/imagetextalternative.js';
import ImageToolbar from '@ckeditor/ckeditor5-image/src/imagetoolbar.js';
import ImageUpload from '@ckeditor/ckeditor5-image/src/imageupload.js';
import Indent from '@ckeditor/ckeditor5-indent/src/indent.js';
import IndentBlock from '@ckeditor/ckeditor5-indent/src/indentblock.js';
import Italic from '@ckeditor/ckeditor5-basic-styles/src/italic.js';
import Link from '@ckeditor/ckeditor5-link/src/link.js';
import List from '@ckeditor/ckeditor5-list/src/list.js';
import ListProperties from '@ckeditor/ckeditor5-list/src/listproperties.js';
import Markdown from '@ckeditor/ckeditor5-markdown-gfm/src/markdown.js';
import Paragraph from '@ckeditor/ckeditor5-paragraph/src/paragraph.js';
import PasteFromOffice from '@ckeditor/ckeditor5-paste-from-office/src/pastefromoffice.js';
import RemoveFormat from '@ckeditor/ckeditor5-remove-format/src/removeformat.js';
import SourceEditing from '@ckeditor/ckeditor5-source-editing/src/sourceediting.js';
import SpecialCharacters from '@ckeditor/ckeditor5-special-characters/src/specialcharacters.js';
import SpecialCharactersArrows from '@ckeditor/ckeditor5-special-characters/src/specialcharactersarrows.js';
import SpecialCharactersCurrency from '@ckeditor/ckeditor5-special-characters/src/specialcharacterscurrency.js';
import SpecialCharactersEssentials from '@ckeditor/ckeditor5-special-characters/src/specialcharactersessentials.js';
import SpecialCharactersLatin from '@ckeditor/ckeditor5-special-characters/src/specialcharacterslatin.js';
import SpecialCharactersMathematical from '@ckeditor/ckeditor5-special-characters/src/specialcharactersmathematical.js';
import Strikethrough from '@ckeditor/ckeditor5-basic-styles/src/strikethrough.js';
import Subscript from '@ckeditor/ckeditor5-basic-styles/src/subscript.js';
import Superscript from '@ckeditor/ckeditor5-basic-styles/src/superscript.js';
import Table from '@ckeditor/ckeditor5-table/src/table.js';
import TableCaption from '@ckeditor/ckeditor5-table/src/tablecaption.js';
import TableCellProperties from '@ckeditor/ckeditor5-table/src/tablecellproperties';
import TableProperties from '@ckeditor/ckeditor5-table/src/tableproperties';
import TableToolbar from '@ckeditor/ckeditor5-table/src/tabletoolbar.js';
import TextPartLanguage from '@ckeditor/ckeditor5-language/src/textpartlanguage.js';
import TextTransformation from '@ckeditor/ckeditor5-typing/src/texttransformation.js';
import Underline from '@ckeditor/ckeditor5-basic-styles/src/underline.js';
//import WordCount from '@ckeditor/ckeditor5-word-count/src/wordcount.js';
import WordCount from './word-cap/src/wordcount.js';
import MathType from '@wiris/mathtype-ckeditor5/src/plugin';

import WProofreader from '@webspellchecker/wproofreader-ckeditor5/src/wproofreader';
import SpeechToTextPlugin from './plugins/speechtotext/plugin.js';

/** NOTE: see `c2c-lesson/ckeditor5/README.md` for build instructions */
class Editor extends InlineEditor {}

function SpecialCharactersSpanish(editor) {
  editor.plugins.get('SpecialCharacters').addItems('Spanish', [
    { character: 'á', title: 'Spanish small letter a with acute' },
    { character: 'é', title: 'Spanish small letter e with acute' },
    { character: 'í', title: 'Spanish small letter i with acute' },
    { character: 'ó', title: 'Spanish small letter o with acute' },
    { character: 'ú', title: 'Spanish small letter u with acute' },
    { character: 'ü', title: 'Spanish small letter u with diaeresis' },
    { character: 'ñ', title: 'Spanish small letter n with tilde' },
    { character: 'Á', title: 'Spanish capital letter A with acute' },
    { character: 'É', title: 'Spanish capital letter E with acute' },
    { character: 'Í', title: 'Spanish capital letter I with acute' },
    { character: 'Ó', title: 'Spanish capital letter O with acute' },
    { character: 'Ú', title: 'Spanish capital letter U with acute' },
    { character: 'Ü', title: 'Spanish capital letter U with diaeresis' },
    { character: 'Ñ', title: 'Spanish capital letter N with tilde' },
    { character: '¿', title: 'Spanish inverted question mark' },
    { character: '¡', title: 'Spanish inverted exclamation mark' }
  ], { label: 'Spanish' });
}

function SpecialCharactersMathematicalExtended(editor) {
  const specialCharacters = editor.plugins.get('SpecialCharacters');
  
  // Add essential Greek letters for mathematics (lowercase + capital Delta only)
  specialCharacters.addItems('Mathematical', [
    // Alpha
    { character: 'α', title: 'Greek small letter alpha' },
    // Beta
    { character: 'β', title: 'Greek small letter beta' },
    // Gamma
    { character: 'γ', title: 'Greek small letter gamma' },
    // Delta (both cases - capital Delta is commonly used)
    { character: 'Δ', title: 'Greek capital letter Delta' },
    { character: 'δ', title: 'Greek small letter delta' },
    // Epsilon
    { character: 'ε', title: 'Greek small letter epsilon' },
    // Pi
    { character: 'π', title: 'Greek small letter pi' },
    // Omega
    { character: 'Ω', title: 'Greek capital letter Omega' },
    // Eta
    { character: 'η', title: 'Greek small letter eta' },
    // Theta
    { character: 'θ', title: 'Greek small letter theta' },
    // Sigma
    { character: 'σ', title: 'Greek small letter sigma' },
    // Rho
    { character: 'ρ', title: 'Greek small letter rho' },
    // Lambda
    { character: 'λ', title: 'Greek small letter lambda' },
    // Mu
    { character: 'μ', title: 'Greek small letter mu' },
    // Nu
    { character: 'ν', title: 'Greek small letter nu' }
  ], { label: 'Mathematical' });
}

// Plugins to include in the build.
Editor.builtinPlugins = [
  Alignment,
  Autoformat,
  BlockQuote,
  Bold,
  CloudServices,
  Essentials,
  FindAndReplace,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  Heading,
  Highlight,
  HorizontalLine,
  HtmlComment,
  Image,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  ListProperties,
  Markdown,
  Paragraph,
  PasteFromOffice,
  RemoveFormat,
  SourceEditing,
  SpecialCharacters,
  SpecialCharactersSpanish,
  SpecialCharactersMathematicalExtended,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  SpecialCharactersLatin,
  SpecialCharactersMathematical,
  Strikethrough,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableProperties,
  TableToolbar,
  TextPartLanguage,
  TextTransformation,
  Underline,
  WordCount,
  MathType,
  WProofreader,
  SpeechToTextPlugin
];

// Editor configuration.
Editor.defaultConfig = {
  toolbar: {
    items: [
      'sourceEditing',
      '|',
      'findAndReplace',
      '|',
      'undo',
      'redo',
      '|',
      'removeFormat',
      '|',
      'outdent',
      'indent',
      'alignment',
      'numberedList',
      'bulletedList',
      '|',
      'textPartLanguage',
      '-',
      'wproofreader',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      '|',
      'subscript',
      'superscript',
      '|',
      'fontBackgroundColor',
      'fontColor',
      'fontFamily',
      'fontSize',
      '-',
      'heading',
      '|',
      'highlight',
      'blockQuote',
      'insertTable',
      'link',
      '|',
      'specialCharacters',
      'MathType',
      'ChemType',
      '|',
      'uploadImage',
      'speechToText'
    ],
    shouldNotGroupWhenFull: true
  },
  language: 'en',
  image: {
    toolbar: [
      'imageStyle:inline',
      'imageStyle:alignLeft',
      'imageStyle:alignCenter',
      'imageStyle:alignRight',
      'imageStyle:block',
      '|',
      'imageTextAlternative',
      '|',
      'resizeImage'
    ],
    resizeOptions: [
      {
        name: 'resizeImage:original',
        value: null,
        label: 'Original'
      },
      {
        name: 'resizeImage:25',
        value: '25',
        label: '25%'
      },
      {
        name: 'resizeImage:50',
        value: '50',
        label: '50%'
      },
      {
        name: 'resizeImage:75',
        value: '75',
        label: '75%'
      }
    ]
  },
  table: {
    contentToolbar: [
      'tableColumn',
      'tableRow',
      'mergeTableCells',
      'tableCellProperties',
      'tableProperties'
    ]
  },
  // this will be overridden via RichTextEditor config when applicable
  wproofreader: {
    // A valid `serviceId` is the key needed for `WProofReader` to function properly.
    // Here, the value for `serviceId` is an empty string by default, but the actual key will be passed
    // via the (c2c-lesson) RichTextEditor config object when applicable.
    serviceId: '',
    srcUrl: 'https://svc.webspellchecker.net/spellcheck31/wscbundle/wscbundle.js'
  }
};

export default Editor;
